import type { ParamValue } from '@/store/params';

/**
 * Rule-based ArduCopter parameter lint — "what's wrong with this vehicle's
 * configuration". Runs entirely client-side over the loaded param table;
 * used by the Params tab health panel and by Diva ("check my params",
 * behaviour-based diagnosis). Rules are deliberately conservative: every
 * finding explains itself and names the fix.
 */
export type LintSeverity = 'critical' | 'warning' | 'info';

export interface LintFinding {
  severity: LintSeverity;
  /** Params involved (clickable in the UI). */
  params: string[];
  title: string;
  detail: string;
  /** Concrete remediation, operator-facing. */
  fix: string;
}

type Table = Record<string, ParamValue>;

function v(t: Table, id: string): number | undefined {
  return t[id]?.value;
}

export function lintParams(t: Table): LintFinding[] {
  const findings: LintFinding[] = [];
  const has = Object.keys(t).length > 0;
  if (!has) return findings;

  // --- Safety-critical ---
  if (v(t, 'ARMING_CHECK') === 0) {
    findings.push({
      severity: 'critical',
      params: ['ARMING_CHECK'],
      title: 'Arming checks disabled',
      detail:
        'ARMING_CHECK = 0 lets the vehicle arm with bad GPS, uncalibrated sensors or EKF errors. Fine on a bench, dangerous anywhere else.',
      fix: 'Set ARMING_CHECK = 1 (all checks) before real flight.',
    });
  }
  if (v(t, 'FRAME_CLASS') === 0) {
    findings.push({
      severity: 'critical',
      params: ['FRAME_CLASS'],
      title: 'Frame class undefined',
      detail: 'The autopilot does not know what airframe it is flying — motor mixing is undefined.',
      fix: 'Set FRAME_CLASS (1 = Quad, 2 = Hexa, …) and reboot.',
    });
  }
  const acc = ['INS_ACCOFFS_X', 'INS_ACCOFFS_Y', 'INS_ACCOFFS_Z'].map((k) => v(t, k));
  if (acc.every((x) => x !== undefined) && acc.every((x) => x === 0)) {
    findings.push({
      severity: 'critical',
      params: ['INS_ACCOFFS_X', 'INS_ACCOFFS_Y', 'INS_ACCOFFS_Z'],
      title: 'Accelerometer never calibrated',
      detail: 'All accel offsets are exactly zero — the six-position calibration has never run.',
      fix: 'Run the accelerometer wizard in Calibrate.',
    });
  }
  const mag = ['COMPASS_OFS_X', 'COMPASS_OFS_Y', 'COMPASS_OFS_Z'].map((k) => v(t, k));
  if (v(t, 'COMPASS_USE') !== 0 && mag.every((x) => x !== undefined) && mag.every((x) => x === 0)) {
    findings.push({
      severity: 'warning',
      params: ['COMPASS_OFS_X', 'COMPASS_OFS_Y', 'COMPASS_OFS_Z'],
      title: 'Compass never calibrated',
      detail:
        'Compass offsets are all zero while the compass is enabled — expect yaw drift and "toilet-bowl" loiter.',
      fix: 'Run the compass wizard in Calibrate, then reboot.',
    });
  }

  // --- Failsafes ---
  if (v(t, 'BATT_MONITOR') === 0) {
    findings.push({
      severity: 'warning',
      params: ['BATT_MONITOR'],
      title: 'Battery monitor disabled',
      detail: 'No voltage/current sensing → no battery failsafe, no capacity estimate.',
      fix: 'Set BATT_MONITOR to match your power module (3 = analog V+I), then reboot.',
    });
  } else {
    if (v(t, 'BATT_LOW_VOLT') === 0 && v(t, 'BATT_LOW_MAH') === 0) {
      findings.push({
        severity: 'warning',
        params: ['BATT_LOW_VOLT', 'BATT_LOW_MAH'],
        title: 'No low-battery threshold',
        detail: 'Both the voltage and capacity triggers for the LOW battery failsafe are off.',
        fix: 'Set BATT_LOW_VOLT (e.g. 3.5 V/cell × cells) or BATT_LOW_MAH.',
      });
    }
    if (v(t, 'BATT_FS_LOW_ACT') === 0) {
      findings.push({
        severity: 'info',
        params: ['BATT_FS_LOW_ACT'],
        title: 'Low-battery failsafe takes no action',
        detail: 'BATT_FS_LOW_ACT = 0 only warns; the vehicle will keep flying until empty.',
        fix: 'Consider 2 (RTL) or 3 (Land).',
      });
    }
  }
  if (v(t, 'FS_THR_ENABLE') === 0) {
    findings.push({
      severity: 'warning',
      params: ['FS_THR_ENABLE'],
      title: 'RC loss failsafe disabled',
      detail: 'If the RC link drops, the vehicle continues on its last stick input.',
      fix: 'Set FS_THR_ENABLE = 1 (RTL on loss) unless flying purely GCS-controlled.',
    });
  }
  if (v(t, 'FS_GCS_ENABLE') === 0) {
    findings.push({
      severity: 'info',
      params: ['FS_GCS_ENABLE'],
      title: 'GCS loss failsafe disabled',
      detail: 'Losing this Console mid-GUIDED-flight leaves the vehicle holding its setpoint.',
      fix: 'Set FS_GCS_ENABLE = 1 for RTL on GCS loss when flying GUIDED missions.',
    });
  }
  if (v(t, 'FENCE_ENABLE') === 0) {
    findings.push({
      severity: 'info',
      params: ['FENCE_ENABLE'],
      title: 'Geofence disabled',
      detail: 'No onboard containment if the vehicle runs away.',
      fix: 'Enable FENCE_ENABLE = 1 with a sane FENCE_RADIUS for field work.',
    });
  }

  // --- Sanity ranges ---
  const wpSpeed = v(t, 'WPNAV_SPEED');
  if (wpSpeed !== undefined && (wpSpeed < 50 || wpSpeed > 2000)) {
    findings.push({
      severity: 'warning',
      params: ['WPNAV_SPEED'],
      title: `Waypoint speed ${wpSpeed < 50 ? 'crawling' : 'extreme'} (${wpSpeed} cm/s)`,
      detail: 'WPNAV_SPEED outside 50–2000 cm/s is almost always a typo (units are cm/s, not m/s).',
      fix: 'Typical copter cruise: 500–1500 cm/s.',
    });
  }
  const angleMax = v(t, 'ANGLE_MAX');
  if (angleMax !== undefined && (angleMax < 1000 || angleMax > 8000)) {
    findings.push({
      severity: 'warning',
      params: ['ANGLE_MAX'],
      title: `Lean-angle limit unusual (${angleMax} cdeg)`,
      detail: 'ANGLE_MAX is in centidegrees. Below 10° the vehicle can barely fight wind; above 80° is aerobatic.',
      fix: 'Default 3000 (30°) suits most airframes.',
    });
  }
  const rtlAlt = v(t, 'RTL_ALT');
  if (rtlAlt !== undefined && rtlAlt < 1500) {
    findings.push({
      severity: 'info',
      params: ['RTL_ALT'],
      title: `RTL altitude low (${(rtlAlt / 100).toFixed(0)} m)`,
      detail: 'RTL returns at this height (cm). Below surrounding obstacles it will fly through them.',
      fix: 'Set RTL_ALT above the tallest obstacle on site, e.g. 3000–4000 (30–40 m).',
    });
  }

  return findings;
}

/**
 * Behaviour-based diagnosis — symptom keywords → suspect params. Used by
 * Diva when the operator describes what the vehicle is doing wrong.
 */
export interface Diagnosis {
  matched: string;
  suspects: string[];
  explanation: string;
}

const SYMPTOMS: { pattern: RegExp; matched: string; suspects: string[]; explanation: string }[] = [
  {
    pattern: /toilet.?bowl|circl(es|ing)|spiral|drift.*(loiter|hover)|yaw.*drift/,
    matched: 'circling / yaw drift in position hold',
    suspects: ['COMPASS_OFS_X', 'COMPASS_OFS_Y', 'COMPASS_OFS_Z', 'COMPASS_ORIENT'],
    explanation:
      'Classic bad-compass signature. Recalibrate the compass away from metal, verify COMPASS_ORIENT matches the mounting, and reboot after.',
  },
  {
    pattern: /wobbl|oscillat|shak|twitch|vibrat/,
    matched: 'oscillation / wobble',
    suspects: ['ATC_RAT_RLL_P', 'ATC_RAT_PIT_P', 'INS_GYRO_FILTER', 'MOT_THST_HOVER'],
    explanation:
      'Rate-PID or vibration issue. Check Health → vibration first; if clean, reduce ATC_RAT_*_P ~20% or run AutoTune.',
  },
  {
    pattern: /won.?t arm|can.?t arm|refuse.*arm|arming fail/,
    matched: 'refuses to arm',
    suspects: ['ARMING_CHECK'],
    explanation:
      'Read the pre-arm message in Health / Logs — it names the exact blocker (GPS, compass variance, battery…). Do NOT zero ARMING_CHECK to bypass it.',
  },
  {
    pattern: /too slow|sluggish|slow.*(goto|waypoint|mission)/,
    matched: 'slow in guided/auto flight',
    suspects: ['WPNAV_SPEED', 'WPNAV_ACCEL', 'ANGLE_MAX'],
    explanation:
      'WPNAV_SPEED caps guided/auto ground speed (cm/s). WPNAV_ACCEL and ANGLE_MAX limit how hard it pushes to reach it.',
  },
  {
    pattern: /battery.*(sag|early|land)|lands? early|fails?afe.*batt/,
    matched: 'battery failsafe firing early',
    suspects: ['BATT_LOW_VOLT', 'BATT_FS_LOW_ACT', 'BATT_VOLT_MULT'],
    explanation:
      'Either the threshold is set for the wrong cell count or the voltage multiplier is miscalibrated — compare Console voltage with a checker.',
  },
  {
    pattern: /altitude.*(hold|drop|climb)|height.*(hold|drift)|baro/,
    matched: 'altitude hold problems',
    suspects: ['INS_ACCOFFS_Z', 'EK3_ALT_M_NSE', 'PILOT_THR_BHV'],
    explanation:
      'Usually vibration or accel-Z calibration. Check Health → vibration; re-run the accel wizard with the vehicle rock-steady.',
  },
  {
    pattern: /gps|glitch|position.*(jump|lost)/,
    matched: 'GPS / position issues',
    suspects: ['GPS_TYPE', 'EK3_SRC1_POSXY', 'FENCE_ENABLE'],
    explanation:
      'Verify sat count + HDOP in Health. Fly away from buildings; consider FENCE as protection while diagnosing.',
  },
];

export function diagnoseBehaviour(text: string): Diagnosis | null {
  const lower = text.toLowerCase();
  for (const s of SYMPTOMS) {
    if (s.pattern.test(lower)) {
      return { matched: s.matched, suspects: s.suspects, explanation: s.explanation };
    }
  }
  return null;
}
